import {TESTS} from '../../../../utils/abacus';
import {getActionLocation, getPageType} from '../../../../utils/edap';

const getOptions = (config, locale) => {
    const {
        url: {prefix},
        site: {name: siteName, domain},
        template,
    } = config;

    const actionLocation = getActionLocation(template);

    const pageType = getPageType(template);
    const systemOptions = {
        site: siteName,
        baseUrl: `https://${domain}`,
        appPrefix: prefix,
        pageType,
        locale,
    };

    const edapOptions = {
        searchQuerySubmittedEventActionLocation: actionLocation,
    };

    const formOptions = {
        autoProgress: 'none',
        layout: 'feature',
    };

    const datesOptions = {
        dateFormatOptions: {
            day: 'numeric',
            month: 'short',
            weekday: 'short',
        },
    };

    const guestsOptions = {
        actionLocation,
    };

    return {
        systemOptions,
        edapOptions,
        formOptions,
        datesOptions,
        guestsOptions,
    };
};

const getSettings = (config) => {
    const {
        isMobile,
        templateType: {isMultiDestination},
    } = config;
    const isOpen = !isMobile && !isMultiDestination;

    const datesSettings = {
        isOpen,
    };

    const mediaSettings = {
        isMobile,
    };

    return {
        mediaSettings,
        datesSettings,
    };
};

const getStores = (config, abacus) => {
    const {template} = config;
    const pageType = getPageType(template);
    const abacusStore = {
        experimentData: abacus,
        experimentNames: [
            TESTS.HA_PTA_SEARCHBOX_AddAgeOfChildren,
            TESTS.HA_PTA_SEARCHFORM_DATES_NUMBER_OF_NIGHTS_SUMMARY,
            TESTS.VRBO_LANDING_ColourContrastOnSearchForm,
            TESTS.VRBO_LANDING_ARIARolesOnSearchForm,
            TESTS.VRBO_LANDING_Scale_LaunchSearchFromGuestPicker,
            TESTS.VRBO_LANDING_DESTINATIONLESS_ERROR_ON_SEARCHFORM,
            TESTS.VRBO_LANDING_SEARCHFORM_DATE_FORMAT,
            TESTS.VRBO_LANDING_SEARCHFORM_DATEPICKER_DAY_OF_WEEK,
        ],
    };
    if (pageType === 'mlp') {
        abacusStore.experimentNames.push(
            TESTS.HA_PTA_SEARCHBOX_HINTTEXTCHOOSINGDATES
        );
    } else {
        abacusStore.experimentNames.push(
            TESTS.VRBO_LANDING_SCALE_HINT_TEXT_WHEN_CHOOSING_DATES
        );
    }

    return {
        abacusStore,
    };
};

const getTranslations = (labels) => {
    const {
        typeahead,
        datePicker,
        guests,
        buttonTitle: searchLabelText,
    } = labels;

    const {
        destinationErrorMsg: errorMessageText,
        destinationplaceholdermsg: placeholderMessage,
        destinationselected: selectedMessage,
        noresultsfound: noResultsFoundMessage,
        recentlyviewed: recentlyViewedLabel,
        recentsearches: recentSearchesMessage,
        resultsfound: resultsFoundMessage,
        trendingdestinations: trendingDestinationsMessage,
        typeairport: typeAirportLabel,
        typecity: typeCityLabel,
        typelandmark: typeLandmarkLabel,
        typemetrostation: typeMetroStationLabel,
        typeneighborhood: typeNeighborhoodLabel,
        typeregion: typeRegionLabel,
        typetrainstation: typeTrainStationLabel,
        where: whereLabel,
    } = typeahead;

    const {
        modalCheckIn: modalTitleStartDateMessage,
        checkIn: startDateLabel,
        checkOut: endDateLabel,
        modalCheckOut: modalTitleEndDateMessage,
        done: datesContinueButtonLabel,
        close: datesCloseButtonLabel,
        labels: datesClearButtonLabel,
        selectDateHintMessage,
        nightsText,
    } = datePicker;

    const {
        adult: adultText,
        adults: adultsText,
        ageOfChildrenSubtitle: ageSelectSubtitle,
        ageOfChildrenTitle: ageSelectTitle,
        ageOfChildTitle: ageSelectTitleSingular,
        applyBtn: guestsApplyButtonLabel,
        child: childText,
        children: childrenText,
        guest: guestText,
        guests: guestsText,
        label: guestsLabel,
        mobileBtn: guestsContinueButtonLabel,
        modalTitle: guestsModalTitle,
        noBtn: petNoLabel,
        petMessage,
        pets: petsText,
        yesBtn: petYesLabel,
    } = guests;

    const destinationTranslations = {
        errorMessageText,
        noResultsFoundMessage,
        placeholderMessage,
        recentSearchesMessage,
        recentlyViewedLabel,
        resultsFoundMessage,
        selectedMessage,
        trendingDestinationsMessage,
        typeAirportLabel,
        typeCityLabel,
        typeLandmarkLabel,
        typeMetroStationLabel,
        typeNeighborhoodLabel,
        typeRegionLabel,
        typeTrainStationLabel,
        whereLabel,
    };

    const datesTranslations = {
        modalTitleStartDateMessage,
        startDateLabel,
        endDateLabel,
        modalTitleEndDateMessage,
        continueButtonLabel: datesContinueButtonLabel,
        clearButtonLabel: datesClearButtonLabel,
        closeButtonLabel: datesCloseButtonLabel,
        selectDateHintMessage,
        nightsText,
    };

    const guestsTranslations = {
        adultText,
        adultsText,
        ageSelectSubtitle,
        ageSelectTitle,
        ageSelectTitleSingular,
        childText,
        childrenText,
        guestText,
        guestsText,
        petsText,
        label: guestsLabel,
        modalTitle: guestsModalTitle,
        petLabel: petsText,
        petNoLabel,
        petYesLabel,
        petMessage,
        applyButtonLabel: guestsApplyButtonLabel,
        continueButtonLabel: guestsContinueButtonLabel,
    };

    const submitTranslations = {
        searchLabelText,
    };

    return {
        destinationTranslations,
        datesTranslations,
        guestsTranslations,
        submitTranslations,
    };
};

const getValues = (destination, search) => {
    const {propertyType, details: destinationDetails = {}} = destination;

    const {
        arrivalDate: startDate,
        departureDate: endDate,
        adults: adultsCount,
        children: childrenCount,
        petIncluded,
    } = search;

    const filterId = !isNaN(parseInt(propertyType, 10)) ? propertyType : null;

    const searchValues = {
        filters: [filterId],
    };

    const destinationValues = {
        destination: destinationDetails.fullName,
        destinationUuid: destinationDetails.lbsId,
    };

    const datesValues = {
        startDate,
        endDate,
    };

    const guestsValues = {
        adultsCount,
        childrenCount,
        petIncluded,
    };

    return {
        searchValues,
        destinationValues,
        datesValues,
        guestsValues,
    };
};

export {getOptions, getSettings, getStores, getTranslations, getValues};
